import type { JSX } from 'react'
import type { OverlapRow } from '../api'
import { monthLabel, yen } from '../format'

// Invariant #8 made visible. Two different findings, kept apart because they
// need different actions:
//
//   overlap_zeroed      the sheet counts pipeline for a client that already
//                       has a booked invoice that month — double-counting the
//                       DB removed (~¥3.1M today).
//   unmatched_prospect  the matcher could not identify the client, so the cell
//                       CANNOT be overlap-checked either way. Fixing it means
//                       adding a client alias — this list is the to-do.

export function OverlapReport({ rows }: { rows: OverlapRow[] }): JSX.Element {
  const zeroed = rows.filter((row) => row.kind === 'overlap_zeroed')
  const unmatched = rows.filter((row) => row.kind === 'unmatched_prospect')

  const total = (subset: OverlapRow[]): string =>
    subset.reduce((sum, row) => sum + Number(row.weightedAmount), 0).toFixed(2)

  return (
    <section className="panel">
      <h2>Pipeline overlap</h2>

      <p className="notice">
        {zeroed.length === 0
          ? 'No pipeline months collide with booked invoices.'
          : `${zeroed.length} pipeline cell${zeroed.length > 1 ? 's' : ''} (${yen(total(zeroed))} weighted) removed as already booked.`}
        {unmatched.length > 0 &&
          ` ${unmatched.length} prospect${unmatched.length > 1 ? 's' : ''} (${yen(total(unmatched))} weighted) could not be matched to a client and were not overlap-checked.`}
      </p>

      {rows.length > 0 && (
        <div className="scroller">
          <table>
            <thead>
              <tr>
                <th scope="col">Finding</th>
                <th scope="col">Month</th>
                <th scope="col">Business line</th>
                <th scope="col">Prospect</th>
                <th scope="col">Expected</th>
                <th scope="col">Weighted</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={`${row.kind}-${row.prospect}-${row.month}-${index}`}>
                  <td>
                    <span className={row.kind === 'overlap_zeroed' ? 'badge' : 'badge warn'}>
                      {row.kind === 'overlap_zeroed' ? 'already booked' : 'unmatched'}
                    </span>
                  </td>
                  <td>{monthLabel(row.month)}</td>
                  <td className="muted">{row.businessLine}</td>
                  <td>{row.prospect}</td>
                  <td>{yen(row.expectedAmount)}</td>
                  <td>{yen(row.weightedAmount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
